<template>
    <div class="live-content">
        <div class="live-title">直播间调试</div>
        <div class="main-content">
            <div class="left-content">
                <div ref="liveVideo" class="live-video" id="live-video"></div>
            </div>
            <div class="right-content">
                <el-scrollbar ref="speechScroll" class="right-top">
                    <div class="show-content">
                        <div v-if="speechLiveLine" class="speak-item speak-item--live">{{ speechLiveLine }}</div>
                        <div class="speak-item" v-for="(item, index) in showText" :key="`speak_${index}`">{{ item }}</div>
                    </div>
                </el-scrollbar>
                <div class="right-bottom">
                    <el-button type="primary" @click="startTest">开始测试</el-button>
                    <el-button type="danger" @click="endTest">结束测试</el-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import globalConfig from "@/utils/config.js";
    import AliyunNlsSpeechTranscriber from "@/utils/aliyunNlsSpeech.js";

    export default {
        name: "LiveDebug",
        data() {
            return {
                watchNum: 0,
                /** 已确认的整句识别结果（与 LiveMain 中 SentenceEnd 一致） */
                showText: [],
                /** 当前一句的中间识别文本（与 LiveMain 中 onIntermediate 一致） */
                speechLiveLine: "",
                speechSDK: null,
            }
        },
        methods: {
            /** 滚动识别结果区域到底部 */
            scrollSpeechToBottom() {
                this.$nextTick(() => {
                    const root = this.$refs.speechScroll && this.$refs.speechScroll.$el;
                    if (!root) return;
                    const wrap = root.querySelector(".el-scrollbar__wrap");
                    if (wrap) {
                        wrap.scrollTop = wrap.scrollHeight;
                    }
                });
            },
            async startTest() {
                if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
                    this.$message.warning("不支持获取媒体接口");
                    return;
                }
                this.$refs.liveVideo.innerHTML = "";
                this.showText = [];
                this.speechLiveLine = "";
                this.setLivePusher();
                try {
                    await this.setSpeech();
                } catch (e) {
                    console.log("语音识别启动失败", e);
                    this.$message.warning(
                        e && String(e.message || e).includes("NO_TOKEN")
                            ? "未获取到有效的阿里云 NLS Token"
                            : "语音识别启动失败，请检查麦克风权限与网络"
                    );
                }
            },
            endTest() {
                this.stopSpeechSdk();
                if (this.$route.query.form) {
                    this.$router.go(-1)
                }
                this.$router.push('liveRoom')
            },
            /** 停止并释放阿里云 NLS 识别实例（对齐 LiveMain） */
            stopSpeechSdk() {
                if (!this.speechSDK) return;
                try {
                    if (typeof this.speechSDK.destroy === "function") {
                        this.speechSDK.destroy();
                    } else if (typeof this.speechSDK.stop === "function") {
                        this.speechSDK.stop();
                    }
                } catch (e) {
                    /* ignore */
                }
                this.speechSDK = null;
                this.speechLiveLine = "";
            },
            /**
             * 启动语音识别：与 LiveRoom → LiveMain.setSpeech 相同，使用阿里云 NLS WebSocket
             */
            async setSpeech() {
                const { aliyunNls = {}, getAliyunNlsToken } = globalConfig;
                let token = "";
                try {
                    token =
                        typeof getAliyunNlsToken === "function"
                            ? await Promise.resolve(getAliyunNlsToken())
                            : "";
                } catch (e) {
                    console.log("阿里云 NLS Token 获取失败", e);
                }
                if (!token || !aliyunNls.appkey) {
                    this.$message.warning("无法启动语音识别：请检查 VUE_APP_ALIYUN_NLS_APPKEY 及 Token 接口");
                    return;
                }
                this.stopSpeechSdk();
                const recognizer = new AliyunNlsSpeechTranscriber({
                    url: aliyunNls.wsUrl,
                    appkey: aliyunNls.appkey,
                    getToken: () =>
                        typeof getAliyunNlsToken === "function" ? Promise.resolve(getAliyunNlsToken()) : Promise.resolve(""),
                    onTranscriptionStarted: () => {
                        console.log("阿里云 NLS：识别任务已启动");
                    },
                    onIntermediate: (intermediateText) => {
                        this.speechLiveLine = intermediateText || "";
                        this.scrollSpeechToBottom();
                    },
                    onSentenceEnd: (finalText) => {
                        const t = (finalText == null ? "" : String(finalText)).trim();
                        if (t) {
                            this.showText.push(t);
                        }
                        this.speechLiveLine = "";
                        this.scrollSpeechToBottom();
                    },
                    onTranscriptionCompleted: (res) => {
                        console.log("阿里云 NLS：识别任务已完成", res);
                    },
                    onTaskFailed: (message) => {
                        console.log("阿里云 NLS：任务失败", message);
                        this.$message.warning("语音识别任务失败，请查看控制台");
                    },
                    onWsError: (err) => {
                        console.log("阿里云 NLS：WebSocket 错误", err);
                    },
                    onWsClose: () => {
                        console.log("阿里云 NLS：WebSocket 已关闭");
                    },
                });
                this.speechSDK = recognizer;
                await recognizer.start();
            },
            setLivePusher() {
                this.livePusher = new TXLivePusher();
                const {livePusher} = this;
                livePusher.setRenderView("live-video");
                livePusher.setVideoQuality("720p");
                livePusher.setAudioQuality("standard");
                livePusher.setProperty("setVideoFPS", 30);
                const gatherStates = [false, false];
                const gatherCallback = (index) => {
                    gatherStates[index] = true;
                    const valid = !gatherStates.includes(false);
                };
                livePusher.startCamera();
                livePusher.startMicrophone();
                livePusher.setObserver({
                    onCaptureFirstAudioFrame: () => gatherCallback(0),
                    onCaptureFirstVideoFrame: () => gatherCallback(1),
                    onWarning: (code, msg) => {
                        const errMsgs = {
                            NotFoundError: "没有找到麦克风或摄像头设备",
                            NotAllowedError: "请确保给予麦克风和摄像头设备的权限",
                            common: "无法获取麦克风和摄像头设备，请尝试刷新网页",
                        };
                        let errmsg = errMsgs[msg];
                        errmsg = errmsg ? errmsg : errMsgs.common;
                    },
                });
            },
        },
        beforeDestroy() {
            this.stopSpeechSdk();
        },
    }
</script>

<style scoped lang="scss">
    .live-content {
        height: 100%;
        width: 100%;
        background-color: #070932;
        display: flex;
        flex-direction: column;

        .live-title {
            padding: 20px;
            font-size: 18px;
            color: #fff;
        }
    }

    .main-content {
        height: 100%;
        width: 100%;
        margin: 10px auto 60px;
        display: flex;
        justify-content: center;
    }

    .left-content {
        height: 100%;
        width: 26%;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        border-radius: 20px;
        background-image: url('../../assets/image/student/live_background.png');
        background-repeat: no-repeat;
        background-size: cover;
        position: relative;
    }

    .right-content {
        width: 40%;
        height: 100%;
        margin-left: 20px;
    }

    .right-top {
        height: 75%;
        background: #0C0E3F;
        border-radius: 10px;
        overflow: hidden;

        ::v-deep .el-scrollbar__wrap {
            height: 100%;
            overflow-x: hidden;

            .el-scrollbar__view {
                height: 100%;
                display: flex;
                flex-direction: column;
            }
        }

        .show-content {
            margin: 20px;
            color: #fff;
            opacity: 0.92;
            line-height: 24px;
            height: calc(100% - 40px);
            word-break: break-word;
        }

        .speak-item--live {
            opacity: 0.65;
            font-style: italic;
            margin-bottom: 8px;
            padding-bottom: 8px;
            border-bottom: 1px dashed rgba(255, 255, 255, 0.2);
        }
    }

    .right-bottom {
        margin-top: 20px;
        height: calc(25% - 20px);
        background: #0C0E3F;
        border-radius: 10px;
        overflow: hidden;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .live-video {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;

        ::v-deep video {
            width: 100%;
            height: 100%;
            object-fit: cover !important;
        }
    }
</style>